import { useMemo } from 'react'
import { useQuery } from 'convex/react'
import { api } from '@backend/_generated/api'
import type { Id } from '@backend/_generated/dataModel'
import type { PositionAssessmentTypeQuestion } from '@/types/api-compat'
import { usePermissions } from '@/features/auth'

/**
 * Custom hook for position questions with grouping functionality
 * Wraps the Convex query with additional data transformation
 */
export const usePositionQuestions = (positionId: string | undefined) => {
  const rawData = useQuery(
    api.positions.getPositionQuestions,
    positionId ? { positionId: positionId as Id<'positions'> } : 'skip',
  )

  const isLoading = rawData === undefined

  // Flatten all questions from all assessment types
  const questions: Array<PositionAssessmentTypeQuestion> = useMemo(() => {
    if (!rawData) return []
    return rawData.flatMap((at) =>
      (at.questions ?? []).map((question) => ({
        ...question,
        managerText: question.managerText ?? '',
        directReportText: question.directReportText ?? '',
      })),
    )
  }, [rawData])

  const { hasRole } = usePermissions()
  const isAdmin = hasRole('admin')
  const isManager = hasRole('manager')

  // Determine what text to show based on role
  const getVisibleText = (question: PositionAssessmentTypeQuestion) => {
    if (isAdmin) {
      return {
        managerText: question.managerText,
        directReportText: question.directReportText,
        showBoth: true,
      }
    } else if (isManager) {
      return {
        managerText: question.managerText,
        directReportText: null,
        showBoth: false,
      }
    } else {
      return {
        managerText: null,
        directReportText: question.directReportText,
        showBoth: false,
      }
    }
  }

  const groupedQuestions = useMemo(() => {
    return questions.reduce(
      (acc, question) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!acc[question.name]) {
          acc[question.name] = {}
        }
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (!acc[question.name][question.subName]) {
          acc[question.name][question.subName] = []
        }
        acc[question.name][question.subName].push({
          ...question,
          visibleText: getVisibleText(question),
        })
        return acc
      },
      {} as Record<
        string,
        Record<
          string,
          Array<
            PositionAssessmentTypeQuestion & {
              visibleText: ReturnType<typeof getVisibleText>
            }
          >
        >
      >,
    )
  }, [questions, isAdmin, isManager])

  return {
    questions,
    groupedQuestions,
    isLoading,
    isError: false,
    error: null,
    isRefetching: false,
    refetch: () => {},
    totalQuestions: questions.length,
  }
}
